import { useEffect, useState } from "react";
import { X, Check, Plus, Edit2, ToggleLeft, ToggleRight, ArrowUp, ArrowDown } from "lucide-react";
import { toast } from "sonner";
import api, { formatApiError } from "@/lib/api";

/**
 * Modal anidado para gestionar las opciones de cada grupo (accept/reject/no_repair).
 * Operaciones soportadas (NO se permite borrar — solo desactivar — porque
 * pueden estar usadas en reparaciones antiguas):
 *  - Añadir (genera id slug a partir del label)
 *  - Editar label
 *  - Toggle active
 *  - Reordenar (sube/baja)
 *
 * Props:
 *  - options: objeto {accept:[], reject:[], no_repair:[]} actual.
 *  - onClose
 *  - onSaved(newOptions): callback con las opciones guardadas.
 *  - isAdmin: bool — si false, los controles quedan deshabilitados.
 */
export default function ManageSituationOptions({ options, onClose, onSaved, isAdmin }) {
  const [tab, setTab] = useState("accept");
  const [draft, setDraft] = useState({ accept: [], reject: [], no_repair: [] });
  const [newLabel, setNewLabel] = useState("");
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    setDraft({
      accept: (options.accept || []).map((o) => ({ ...o })),
      reject: (options.reject || []).map((o) => ({ ...o })),
      no_repair: (options.no_repair || []).map((o) => ({ ...o })),
    });
  }, [options]);

  const slug = (s) =>
    s.normalize("NFD").replace(/[\u0300-\u036f]/g, "")
      .toLowerCase().replace(/[^a-z0-9]+/g, "_")
      .replace(/^_+|_+$/g, "").slice(0, 40) || `opt_${Date.now()}`;

  const addOpt = () => {
    const label = newLabel.trim();
    if (!label) return;
    const lst = draft[tab].slice();
    let id = slug(label);
    // evitar colisión con ids existentes
    if (lst.some((o) => o.id === id)) id = `${id}_${Date.now() % 10000}`;
    lst.push({ id, label, active: true, order: lst.length });
    setDraft({ ...draft, [tab]: lst });
    setNewLabel("");
  };

  const editLabel = (idx, val) => {
    const lst = draft[tab].slice();
    lst[idx] = { ...lst[idx], label: val };
    setDraft({ ...draft, [tab]: lst });
  };

  const toggleActive = (idx) => {
    const lst = draft[tab].slice();
    lst[idx] = { ...lst[idx], active: !lst[idx].active };
    setDraft({ ...draft, [tab]: lst });
  };

  const move = (idx, dir) => {
    const lst = draft[tab].slice();
    const target = idx + dir;
    if (target < 0 || target >= lst.length) return;
    [lst[idx], lst[target]] = [lst[target], lst[idx]];
    setDraft({ ...draft, [tab]: lst });
  };

  const save = async () => {
    setSaving(true);
    try {
      const r = await api.put("/settings/situation-options", draft);
      onSaved(r.data);
      toast.success("Opciones guardadas");
      onClose();
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setSaving(false);
    }
  };

  const TABS = [
    { id: "accept", label: "Acepta reparación", color: "bg-emerald-600" },
    { id: "reject", label: "Rechaza reparación", color: "bg-red-600" },
    { id: "no_repair", label: "Sin reparación", color: "bg-amber-600" },
  ];

  const lst = draft[tab] || [];

  return (
    <div
      className="fixed inset-0 z-50 bg-black/50 flex items-center justify-center p-4"
      onClick={onClose}
      data-testid="manage-situation-options"
    >
      <div
        className="bg-white border border-[#111110] shadow-hard w-full max-w-2xl max-h-[90vh] flex flex-col"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="flex items-center justify-between px-4 py-3 border-b border-[#E5E5E0]">
          <div>
            <div className="font-mono text-[10px] uppercase tracking-widest text-[#525252]">
              // Iter 69
            </div>
            <div className="font-display font-bold text-base">Gestionar opciones de situación</div>
          </div>
          <button onClick={onClose} className="p-1 hover:bg-[#F7F7F4]" data-testid="manage-options-close">
            <X size={16} />
          </button>
        </div>

        <div className="flex border-b border-[#E5E5E0] bg-[#F7F7F4]">
          {TABS.map((t) => (
            <button
              key={t.id}
              onClick={() => setTab(t.id)}
              data-testid={`manage-tab-${t.id}`}
              className={`flex-1 px-3 py-2 text-xs font-semibold ${
                tab === t.id ? `${t.color} text-white` : "text-[#525252] hover:bg-white"
              }`}
            >
              {t.label}
            </button>
          ))}
        </div>

        <div className="flex-1 overflow-y-auto p-4 space-y-2">
          {lst.length === 0 && (
            <div className="text-sm text-[#525252] text-center py-6">
              Aún no hay opciones en este grupo.
            </div>
          )}
          {lst.map((opt, i) => (
            <div
              key={opt.id}
              data-testid={`manage-opt-${opt.id}`}
              className={`border ${opt.active ? "border-[#E5E5E0] bg-white" : "border-[#E5E5E0] bg-[#F7F7F4] opacity-70"} px-3 py-2 flex items-center gap-2`}
            >
              <input
                value={opt.label}
                onChange={(e) => editLabel(i, e.target.value)}
                disabled={!isAdmin}
                data-testid={`manage-opt-label-${opt.id}`}
                className="flex-1 bg-transparent border-b border-[#E5E5E0] focus:border-[#6E8B3D] outline-none px-1 py-1 text-sm"
              />
              <span className="font-mono text-[9px] text-[#A8A8A0]">{opt.id}</span>
              <button
                onClick={() => move(i, -1)}
                disabled={!isAdmin || i === 0}
                title="Subir"
                data-testid={`manage-opt-up-${opt.id}`}
                className="p-1 border border-[#E5E5E0] hover:border-[#111110] disabled:opacity-30"
              >
                <ArrowUp size={12} />
              </button>
              <button
                onClick={() => move(i, +1)}
                disabled={!isAdmin || i === lst.length - 1}
                title="Bajar"
                data-testid={`manage-opt-down-${opt.id}`}
                className="p-1 border border-[#E5E5E0] hover:border-[#111110] disabled:opacity-30"
              >
                <ArrowDown size={12} />
              </button>
              <button
                onClick={() => toggleActive(i)}
                disabled={!isAdmin}
                title={opt.active ? "Desactivar" : "Activar"}
                data-testid={`manage-opt-toggle-${opt.id}`}
                className="p-1 border border-[#E5E5E0] hover:border-[#111110] disabled:opacity-30"
              >
                {opt.active ? <ToggleRight size={14} className="text-emerald-600" /> : <ToggleLeft size={14} className="text-[#A8A8A0]" />}
              </button>
            </div>
          ))}
        </div>

        {isAdmin && (
          <div className="border-t border-[#E5E5E0] p-3 bg-[#F7F7F4] flex items-center gap-2">
            <input
              value={newLabel}
              onChange={(e) => setNewLabel(e.target.value)}
              onKeyDown={(e) => e.key === "Enter" && addOpt()}
              placeholder="Nueva opción..."
              data-testid="manage-new-label"
              className="flex-1 ds-card focus:border-[#6E8B3D] outline-none px-3 py-2 text-sm"
            />
            <button
              onClick={addOpt}
              data-testid="manage-add"
              className="inline-flex items-center gap-1 bg-[#111110] hover:bg-black text-white px-3 py-2 text-xs font-semibold"
            >
              <Plus size={12} /> Añadir
            </button>
          </div>
        )}

        <div className="border-t border-[#E5E5E0] p-3 flex justify-end gap-2">
          <button
            onClick={onClose}
            className="border border-[#E5E5E0] hover:border-[#111110] px-3 py-2 text-xs"
          >
            Cancelar
          </button>
          <button
            onClick={save}
            disabled={saving || !isAdmin}
            data-testid="manage-save"
            className="inline-flex items-center gap-1 bg-[#6E8B3D] hover:bg-[#556C2E] text-white px-4 py-2 text-xs font-semibold disabled:opacity-50"
          >
            <Check size={12} /> {saving ? "Guardando..." : "Guardar opciones"}
          </button>
        </div>
      </div>
    </div>
  );
}
